import { faBullhorn } from "@fortawesome/free-solid-svg-icons/faBullhorn";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons/faFileAlt";
import isEmpty from "lodash/isEmpty";
import React, { useCallback, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { AtiraSelect } from "../../../components/AtiraSelect";
import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { useStepContext } from "../../../components/stepper/StepperContext";
import { useStepperRegisterValidation } from "../../../components/stepper/useStepperRegisterValidation";
import { CreateCampaignDripDto } from "../../../model/campaign/dto/CreateCampaignDripDto";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { campaignSliceSelectors } from "../../../redux/campaign/campaign.selector";
import { formSliceSelectors } from "../../../redux/forms/form.selector";
import { useAppSelector } from "../../../redux/store";
import { Spacing } from "../../../theme/Spacing";
import { FormType } from "../../../model/form/types/FormType.enum";

const Container = styled(Flex)`
  align-items: center;
  gap: ${Spacing.xl};
  width: 50%;
  flex-wrap: wrap;
  align-self: flex-start;
`;

export const DripCampaignCreateEditStepBasicInfo: React.FC = () => {
  const { t } = useTranslation();
  const { currentStep } = useStepContext();

  const { control, watch } = useFormContext<CreateCampaignDripDto>();

  const [name, formId] = watch(["name", "formId"]);

  const canGoNext = useCallback(() => {
    return (
      name?.length >= Lengths.MIN_3 &&
      name?.length <= Lengths.DESCRIPTION &&
      formId?.length > 0
    );
  }, [name, formId]);

  useStepperRegisterValidation(currentStep, canGoNext);

  const forms = useAppSelector(formSliceSelectors.selectForms).filter(
    (f) => !f.orphan && f.type === FormType.ENTRY,
  );
  const selectedCampaign = useAppSelector(
    campaignSliceSelectors.selectSelectedCampaign,
  );

  const editMode =
    !isEmpty(selectedCampaign) && !isEmpty(selectedCampaign?._id);

  const formOptions = useMemo(() => {
    return forms.map((f) => ({
      label: f.title,
      value: f._id,
    }));
  }, [forms]);

  return (
    <Container>
      <Controller
        name="name"
        control={control}
        rules={{
          required: {
            value: true,
            message: t("common.this_field_required"),
          },
          maxLength: {
            value: Lengths.DESCRIPTION,
            message: t("error.length.less", {
              name: t("campaigns.dripCampaign.step.basicInfo.name.title"),
              length: Lengths.DESCRIPTION,
            }),
          },
          minLength: {
            value: Lengths.MIN_3,
            message: t("error.length.more", {
              name: t("campaigns.dripCampaign.step.basicInfo.name.title"),
              length: Lengths.MIN_3,
            }),
          },
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Flex flexDirection="column" gap="m" width={"100%"}>
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faBullhorn} color="main" size="xl" />

              <Text fontSize="l" fontWeight={"bold"} color="main">
                {t("campaigns.dripCampaign.step.basicInfo.name.title")}
              </Text>
            </Flex>

            <Text color="darkerSub">
              {t("campaigns.dripCampaign.step.basicInfo.name.description")}
            </Text>

            <Input
              onChange={onChange}
              value={value}
              valid={!error?.message}
              errorMessage={error?.message}
              required
              maxLength={Lengths.DESCRIPTION}
              // dataTestId={TestIDs.forms.createEdit.steps.step1.title.input}
            />
          </Flex>
        )}
      />

      <Controller
        control={control}
        name="formId"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Flex flexDirection="column" gap="m" width={"100%"}>
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faFileAlt} color="main" size="xl" />

              <Text fontSize="l" fontWeight={"bold"} color="main">
                {t("campaigns.dripCampaign.step.basicInfo.form.title")}
              </Text>
            </Flex>

            <Text color="darkerSub">
              {t("campaigns.dripCampaign.step.basicInfo.form.description")}
            </Text>

            <AtiraSelect
              options={formOptions}
              value={formOptions?.find((f) => f.value === value)}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
            />

            {editMode ? (
              <Info
                info={t(
                  "campaigns.dripCampaign.step.basicInfo.form-change.info",
                )}
                color="warning"
              />
            ) : null}
          </Flex>
        )}
      />
    </Container>
  );
};
